import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { Steps, Button, Icon, Spin, Tooltip, message, Modal, Alert } from 'antd'
import './styles.scss'
import NewHeader from '../../components/NewHeader'
import UploadDocument from './components/UploadDocument'
import ShotList from './components/ShotList'
import ImageGeneration from './components/ImageGeneration'
import VideoGeneration from './components/VideoGeneration'
import VideoStyle from './components/VideoStyle'
import CreativeDirection from './components/CreativeDirection'
import NarrationVoiceover from './components/Voiceover/NarrationVoiceover'
import CharacterVoiceover from './components/Voiceover/CharacterVoiceover'
import { useImageGeneration } from './components/ImageGeneration/hooks/useImageGeneration'
import { useVideoGeneration } from './components/VideoGeneration/hooks/useVideoGeneration'
import { useFinalVideoGeneration } from './components/VideoGeneration/hooks/useFinalVideoGeneration'
import {
  useCharacters,
  useVideoDataPersistence,
  useLocationReferences,
  useAudioClustering,
  useShotsGeneration,
  useStepNavigation,
  useVoiceManagement,
  useCalculateDurations,
  useDraftShotsGeneration,
  usePropReferences,
  useWardrobe,
  defaultData
} from './hooks'
import {
  constructAssemblyPayload,
  formatDurationOutput
} from './utils/videoUtils'
import {
  clearSceneCurrentAudio,
  normalizeSceneAudioHistory
} from './utils/audioHistory'

import {
  changeMachineState,
  createAiVideo,
  distributeStoryVoiceover,
  getTaskStatus,
  updateAiVideo
} from '../../services/api/aiVideoGeneration'
import history from '../../history'
import { ModeSelectionPage, DraftModePage } from './components/DraftMode'
import GenerationStatusModal from './components/VideoGeneration/components/GenerationStatusModal'
import AIVideoModal from './components/VideoGeneration/components/FinalVideo'
import VoiceSelectionRequiredModal from './components/Voiceover/VoiceSelectionRequiredModal'
import FinalVideoDisplay from './components/FinalVideoDisplay/FinalVideoDisplay'

const { Step } = Steps

const CREATION_MODE_OPTIONS = [
  {
    key: 'basic',
    icon: 'play-circle',
    title: 'Basic',
    badge: 'Standard',
    description:
      'Standard flow: story, voiceover, visuals, shot list, images, video.',
    details: [
      'Story',
      'Voiceover',
      'Visual setup',
      'Shot list',
      'Images',
      'Video'
    ]
  },
  {
    key: 'advanced',
    icon: 'compass',
    title: 'Advanced',
    badge: 'AI Director',
    description:
      'Basic flow plus one AI Director chat before the shot list.',
    details: [
      'Story',
      'Voiceover',
      'Visual setup',
      'AI Director chat',
      'Shot list',
      'Images',
      'Video'
    ]
  }
]

const CreationModeSelection = ({
  selectedMode,
  onSelectMode,
  onContinue,
  onGoBack
}) => (
  <div className="ai-video-mode-selection">
    <div className="ai-video-mode-selection__shell">
      <button
        type="button"
        className="ai-video-mode-selection__back"
        onClick={onGoBack}
        aria-label="Back to video list"
      >
        <Icon type="arrow-left" />
      </button>

      <div className="ai-video-mode-selection__header">
        <div className="ai-video-mode-selection__eyebrow">
          <Icon type="video-camera" />
          <span>AI Video Setup</span>
        </div>
        <h1>Choose Basic or Advanced</h1>
        <p>
          Advanced only adds the AI Director chat step. Everything else stays
          the same.
        </p>
      </div>

      <div className="ai-video-mode-selection__cards">
        {CREATION_MODE_OPTIONS.map(option => {
          const isSelected = selectedMode === option.key

          return (
            <button
              type="button"
              className={`ai-video-mode-selection__card${isSelected ? ' ai-video-mode-selection__card--selected' : ''
                }`}
              key={option.key}
              onClick={() => onSelectMode(option.key)}
            >
              <span className="ai-video-mode-selection__card-check">
                {isSelected ? <Icon type="check-circle" /> : null}
              </span>
              <span className="ai-video-mode-selection__card-icon">
                <Icon type={option.icon} />
              </span>
              <span className="ai-video-mode-selection__card-title">
                <strong>{option.title}</strong>
                <em>{option.badge}</em>
              </span>
              <span className="ai-video-mode-selection__card-description">
                {option.description}
              </span>
              <span className="ai-video-mode-selection__card-list">
                {option.details.map(detail => (
                  <span key={detail}>
                    <Icon type="check" />
                    {detail}
                  </span>
                ))}
              </span>
            </button>
          )
        })}
      </div>

      <div className="ai-video-mode-selection__footer">
        <Button
          type="primary"
          size="large"
          disabled={!selectedMode}
          onClick={onContinue}
        >
          Continue
          <Icon type="right" />
        </Button>
      </div>
    </div>
  </div>
)

const VideoAi = props => {
  const videoGenerationRef = useRef(null)
  const imageGenerationRef = useRef(null)

  const [showProcessShots, setShowProcessShots] = useState(false)
  const [isFineTunedLoading, setIsFineTunedLoading] = useState(false)
  const [calculationStarting, setCalculationStarting] = useState(false)
  const [formData, setFormData] = useState({
    ...defaultData,
    durationsReady: false
  })
  const formDataRef = useRef(formData)
  const isCalculatingRef = useRef(false)
  const videoStepCalculationKeyRef = useRef(null)
  const initialLoadRef = useRef(true)
  const fetchStartedRef = useRef(false)
  const locationGenerationTriggeredRef = useRef(false)
  const propGenerationTriggeredRef = useRef(false)
  const audioClusteringTriggeredRef = useRef(false)

  // Draft Mode State
  const [showModeSelectionPage, setShowModeSelectionPage] = useState(false)
  const [pendingCreationMode, setPendingCreationMode] = useState(null)
  const [showVoiceWarningModal, setShowVoiceWarningModal] = useState(false)
  const [voiceWarningData, setVoiceWarningData] = useState({
    missingNarrator: false,
    charactersWithoutVoice: []
  })
  const [isDraftMode, setIsDraftMode] = useState(false)
  const [showEditOptions, setShowEditOptions] = useState(false)
  const [pendingStoryAction, setPendingStoryAction] = useState(null)
  const [pendingStoryText, setPendingStoryText] = useState('')
  const [pendingStoryLanguage, setPendingStoryLanguage] = useState(null)
  const [isStoryCloneDistributing, setIsStoryCloneDistributing] = useState(
    false
  )
  const [storyCloneStep, setStoryCloneStep] = useState('idle')
  const [
    showCreativeShotRegenerateModal,
    setShowCreativeShotRegenerateModal
  ] = useState(false)
  const [
    isCreativeShotCloneRegenerating,
    setIsCreativeShotCloneRegenerating
  ] = useState(false)

  useEffect(() => {
    formDataRef.current = formData
  }, [formData])

  const { videoId, initialStep, handleGoBack } = props

  // Image and Video providers
  const [imageProvider, setImageProvider] = useState('DeepAI')
  const [isImageGenerationLoading, setIsImageGenerationLoading] = useState(
    false
  )
  const [videoProvider, setVideoProvider] = useState('luma')

  // UI State for Modals
  const [showGenerationModal, setShowGenerationModal] = useState(false)
  const [showFinalVideoModal, setShowFinalVideoModal] = useState(false)
  const isAdvancedCreationMode = true || formData.aiVideoFlowMode === 'advanced'

  // Define steps configuration
  const steps = useMemo(() => {
    const stepList = [
      {
        key: 'uploadDocument',
        title: 'Upload',
        icon: 'file-text',
        component: UploadDocument
      }
    ]

    // Step 2: Narration Voiceover (only if not dialogue only)
    if (formData.audioMode !== 'dialogue') {
      stepList.push({
        key: 'narrationVoiceover',
        title: 'Narration',
        icon: 'sound',
        component: NarrationVoiceover
      })
    }

    stepList.push(
      {
        key: 'videoStyle',
        title: 'Visual Setup',
        icon: 'camera',
        component: VideoStyle
      }
    )

    if (isAdvancedCreationMode) {
      stepList.push({
        key: 'creativeDirection',
        title: 'AI Director',
        icon: 'compass',
        component: CreativeDirection
      })
    }

    stepList.push(
      {
        key: 'shotList',
        title: 'Shot List',
        icon: 'ordered-list',
        component: ShotList
      }
    )

    // Step 5: Character Voiceover (only if not narration only)
    if (formData.audioMode !== 'narration') {
      stepList.push({
        key: 'characterVoiceover',
        title: 'Characters',
        icon: 'user',
        component: CharacterVoiceover
      })
    }

    stepList.push(
      {
        key: 'imageGeneration',
        title: 'Images',
        icon: 'picture',
        component: ImageGeneration
      },
      {
        key: 'videoGeneration',
        title: 'Video',
        icon: 'video-camera',
        component: VideoGeneration
      }
    )

    if (formData.finalVideo) {
      stepList.push({
        key: 'finalVideo',
        title: 'Final Result',
        icon: 'play-circle',
        component: FinalVideoDisplay
      })
    }

    return stepList
  }, [formData.finalVideo, formData.audioMode, isAdvancedCreationMode])

  // Helper to set scenes in formData
  const setScenes = action => {
    setFormData(prev => {
      const currentScenes = prev.shotList || []
      const newScenes =
        typeof action === 'function' ? action(currentScenes) : action
      return { ...prev, shotList: newScenes }
    })
  }

  // Step Navigation Hook
  const {
    currentStep,
    stepsValidation,
    handleNext,
    handlePrevious,
    navigateToStep,
    updateStepValidation,
    setStepsValidation,
    getPreviousNextStepTitles,
    shouldShowNextButton,
    isAllScenesGenerated
  } = useStepNavigation({
    videoId,
    initialStep,
    steps,
    isFetchingVideoData: false // Will be set properly below
  })

  // Location References Hook
  const {
    isGeneratingLocationReference,
    locationGenerationProgress,
    locationGenerationStatus,
    generateLocationMap,
    handleAddLocationReference,
    handleEditLocationReference,
    handleBatchUpdateLocations
  } = useLocationReferences({
    formData,
    setFormData,
    videoId // Pass videoId
  })

  // Prop References Hook
  const {
    isGeneratingPropReference,
    propGenerationProgress,
    propGenerationStatus,
    generatePropMap,
    handleAddPropReference,
    handleEditPropReference,
    handleBatchUpdateProps
  } = usePropReferences({
    formData,
    setFormData,
    videoId // Pass videoId
  })

  // Shots Generation Hook
  const shotsHook = useShotsGeneration({
    formData,
    setFormData,
    videoId, // Pass videoId
    parseJSONSafely: (val, maxDepth = 5) => {
      if (val === null || val === undefined) return null
      let result = val
      let depth = 0
      while (typeof result === 'string' && depth < maxDepth) {
        try {
          result = JSON.parse(result)
          depth++
        } catch {
          break
        }
      }
      return result
    }
  })

  const {
    isFetchingShots,
    shotsGenerationProgress,
    formatSceneObj,
    fetchShots
  } = shotsHook

  // Audio Clustering Hook
  const {
    isClusteringAudio,
    audioClusteringProgress,
    audioClusteringStatus,
    initiateAudioClustering,
    formatShotListForClustering,
    resumeAudioPolling,
    stopAudioClustering
  } = useAudioClustering({
    formData,
    setFormData,
    formatSceneObj,
    videoId // Pass videoId
  })

  const handleStopAudioGeneration = useCallback(() => {
    stopAudioClustering()
    setFormData(prev => ({
      ...prev,
      audio: {
        ...prev.audio,
        id: null,
        status: 'failed'
      }
    }))
    audioClusteringTriggeredRef.current = false
    message.info('Audio generation stopped')
  }, [stopAudioClustering, setFormData])

  const handleRetryAudioClustering = useCallback(() => {
    if (!formData?.selectedVoice) {
      message.warning('Please select a narration voice first')
      return
    }

    if (!formData?.shotList?.length) {
      message.warning('No scenes are available to cluster yet')
      return
    }

    const formattedData = formatShotListForClustering(formData.shotList)

    setFormData(prev => ({
      ...prev,
      shotList: (prev.shotList || []).map(scene => ({
        ...clearSceneCurrentAudio(scene),
        shots: (scene.shots || []).map(shot => ({
          ...shot,
          audio_duration: null
        }))
      })),
      audio: {
        ...prev.audio,
        id: null,
        status: null
      }
    }))

    audioClusteringTriggeredRef.current = true
    initiateAudioClustering(formattedData, null, {
      voiceId: formData.selectedVoice
    })
  }, [
    formData?.selectedVoice,
    formData?.shotList,
    formatShotListForClustering,
    initiateAudioClustering,
    setFormData
  ])

  // Connections and side effects are handled below

  // Video Data Persistence Hook
  const {
    isFetchingVideoData,
    isSavingVideoData,
    isCloning,
    fetchVideoData,
    handleSaveVideoData,
    handleUpdateVideoData,
    cloneVideoWithStyle,
    getPayload,
    parseJSONSafely,
    error
  } = useVideoDataPersistence({
    videoId,
    formData,
    setFormData,
    setStepsValidation,
    steps,
    currentStep,
    onLocationMapGenerate: generateLocationMap
  })

  // Voice Management Hook
  const { voices, setVoices, handleVoiceChange } = useVoiceManagement({
    formData,
    setFormData,
    videoId, // Pass videoId
    onAudioClustering: initiateAudioClustering,
    formatShotListForClustering
  })

  // Characters Hook
  const {
    characters,
    setCharacters,
    isFetchingCharacters,
    fetchCharacters
  } = useCharacters({
    videoId, // Pass videoId
    onCharactersLoaded: chars => {
      setFormData(prev => ({ ...prev, characterList: chars }))
    }
  })

  // Duration Calculation Hook
  const {
    calculate: callCalculateDurations,
    isCalculating: isCalculatingDurations,
    progress: calculationProgress
  } = useCalculateDurations({ videoId }) // Pass videoId

  // Wardrobe Hook
  const {
    fetchLibrary: fetchWardrobeLibrary,
    isLoading: isWardrobeLoading
  } = useWardrobe({ videoId }) // Pass videoId

  // Draft Shots Generation Hook
  const draftShotsHook = useDraftShotsGeneration({
    parseJSONSafely,
    videoId // Pass videoId
  })

  // Fingerprint of data that affects durations
  const durationFingerprint = useMemo(() => {
    if (!formData.shotList) return ''
    try {
      return JSON.stringify(
        formData.shotList.map(scene => ({
          id: scene.id,
          voiceover: scene.aggregated_voiceover,
          shots: (scene.shots || []).map(shot => ({
            id: shot.id,
            text: shot.voiceover_text
          }))
        }))
      )
    } catch (e) {
      return ''
    }
  }, [formData.shotList])

  // Reset durationsReady if data fingerprint changes
  useEffect(() => {
    if (formData.durationsReady) {
      setFormData(prev => ({ ...prev, durationsReady: false }))
    }
  }, [durationFingerprint])

  const handleStartDurationCalculation = useCallback(async () => {
    const currentData = formDataRef.current
    if (
      isCalculatingRef.current ||
      !currentData.shotList ||
      currentData.shotList.length === 0
    )
      return

    isCalculatingRef.current = true
    setCalculationStarting(true)
    try {
      const payload = constructAssemblyPayload(
        currentData.shotList,
        currentData.locationMap
      )
      // Exclude location_map from shooting_list as per requirement
      const {
        location_map,
        ...shootingListWithoutLocationMap
      } = payload.clustered_shooting_list

      const output = await callCalculateDurations(
        {
          shooting_list: shootingListWithoutLocationMap,
          shot: null,
          character_map: currentData.characterMap || null,
          outfit_map: currentData.outfitMap || null,
          wardrobe: currentData.wardrobe || null,
          outfits_reference: currentData.outfits_reference || null
        },
        formatDurationOutput,
        currentData.shotList
      )

      if (output) {
        setFormData(prev => ({
          ...prev,
          calculatedShotList: output,
          durationsReady: true
        }))
      }
    } catch (error) {
      console.error('Duration calculation failed:', error)
    } finally {
      isCalculatingRef.current = false
      setCalculationStarting(false)
    }
  }, [callCalculateDurations])

  // Trigger duration calculation once each time the user enters Video Generation.
  useEffect(() => {
    const isVideoStep = steps[currentStep]?.key === 'videoGeneration'
    const isDialogueMode = formData.audioMode === 'dialogue'

    if (!isVideoStep) {
      videoStepCalculationKeyRef.current = null
      return
    }

    const calculationKey = `${durationFingerprint}:${formData.shotList
      ?.length || 0}`

    if (isDialogueMode || formData.shotList?.length === 0) {
      return
    }

    if (
      !isCalculatingDurations &&
      !isCalculatingRef.current &&
      videoStepCalculationKeyRef.current !== calculationKey
    ) {
      videoStepCalculationKeyRef.current = calculationKey

      if (formData.durationsReady || formData.calculatedShotList) {
        setFormData(prev => ({
          ...prev,
          durationsReady: false,
          calculatedShotList: null
        }))
      }

      handleStartDurationCalculation()
    }
  }, [
    currentStep,
    isCalculatingDurations,
    formData.durationsReady,
    formData.calculatedShotList,
    formData.shotList?.length,
    durationFingerprint,
    handleStartDurationCalculation,
    steps.length
  ])

  // Image Generation Hook
  const {
    generateAllImages,
    handleRegenerateImage,
    retryFailedImages,
    handleFileUpload,
    isUploading: isUploadingImage
  } = useImageGeneration({
    scenes: formData.shotList || [],
    setScenes,
    formData,
    imageProvider,
    setIsLoading: setIsImageGenerationLoading,
    videoId // Pass videoId
  })

  const {
    videoGenerating,
    generatingVideoForAllScenes,
    handleGenerateFullVideo: generateAllVideos,
    handleRegenerateVideo,
    handleStopVideoGeneration,
    handleRetryFailedVideos,
    updateShotDataRef
  } = useVideoGeneration({
    scenes: formData.shotList || [],
    setScenes,
    formData,
    videoProvider,
    videoId // Pass videoId
  })

  // Final Video Generation Hook (New)
  const {
    isGeneratingFinalVideo,
    finalVideoError,
    generateFinalVideo,
    setFinalVideoError
  } = useFinalVideoGeneration({
    formData,
    setFormData,
    videoId // Pass videoId
  })

  // Sync modal visibility with generation state
  useEffect(() => {
    if (isGeneratingFinalVideo) {
      setShowGenerationModal(true)
    }
  }, [isGeneratingFinalVideo])

  // Automatically open video player when generation completes
  const wasGeneratingRef = useRef(false)
  useEffect(() => {
    if (isGeneratingFinalVideo) {
      wasGeneratingRef.current = true
    } else if (
      wasGeneratingRef.current &&
      formData.finalVideo &&
      !finalVideoError
    ) {
      wasGeneratingRef.current = false
      // Instead of showing modal, navigate to the final step
      if (steps.some(s => s.key === 'finalVideo')) {
        navigateToStep(steps.length - 1)
      }
    }
  }, [
    isGeneratingFinalVideo,
    formData.finalVideo,
    finalVideoError,
    steps,
    navigateToStep
  ])

  // Fetch video data on mount/videoId change
  useEffect(() => {
    if (videoId) {
      fetchVideoData()
    }
  }, [videoId])

  // Fetch characters if needed
  useEffect(() => {
    const shouldFetchCharacters =
      !formData.characterList || formData.characterList.length === 0
    if (shouldFetchCharacters && videoId) {
      fetchCharacters()
    }
  }, [formData.characterList, videoId])

  // Reset initialLoadRef when data is successfully fetched or if it's a new video
  useEffect(() => {
    if (isFetchingVideoData) {
      fetchStartedRef.current = true
    }
  }, [isFetchingVideoData])

  useEffect(() => {
    if (!videoId) {
      initialLoadRef.current = false
    } else if (fetchStartedRef.current && !isFetchingVideoData) {
      initialLoadRef.current = false
    }
  }, [isFetchingVideoData, videoId])

  // Auto-save on form data change
  useEffect(() => {
    if (!initialLoadRef.current) {
      handleSaveVideoData()
    }
  }, [formData])

  // Redirect to draft mode if there is a pending draft on initial load
  useEffect(() => {
    if (
      !isFetchingVideoData &&
      formData.draft &&
      formData.draft.id &&
      formData.draft.status === 'pending' &&
      !isDraftMode
    ) {
      setIsDraftMode(true)
    }
  }, [isFetchingVideoData, formData.draft, isDraftMode])

  // Redirect to video generation step if final video is pending (New)
  useEffect(() => {
    if (
      !isFetchingVideoData &&
      !initialLoadRef.current &&
      formData.video?.status === 'pending' &&
      steps[currentStep]?.key !== 'videoGeneration'
    ) {
      // Find index of videoGeneration
      const videoIndex = steps.findIndex(s => s.key === 'videoGeneration')
      if (videoIndex !== -1) navigateToStep(videoIndex)
    }
  }, [
    isFetchingVideoData,
    formData.video?.status,
    currentStep,
    steps.length,
    navigateToStep
  ])

  // Trigger location generation if user moves past uploadDocument step and references are missing
  useEffect(() => {
    const isUploadStep = steps[currentStep]?.key === 'uploadDocument'
    const hasLocationMap =
      formData.locationMap && Object.keys(formData.locationMap).length > 0
    const hasLocationReference =
      formData.locationReference &&
      Object.keys(formData.locationReference).length > 0
    const hasPendingDraft =
      formData.draft && formData.draft.id && formData.draft.status === 'pending'

    if (hasLocationReference || !hasLocationMap) {
      locationGenerationTriggeredRef.current = false
    }

    if (
      !isUploadStep &&
      !isDraftMode &&
      !hasPendingDraft &&
      hasLocationMap &&
      !hasLocationReference &&
      !isGeneratingLocationReference &&
      !locationGenerationTriggeredRef.current
    ) {
      locationGenerationTriggeredRef.current = true
      generateLocationMap(formData.locationMap)
    }
  }, [
    currentStep,
    isDraftMode,
    isGeneratingLocationReference,
    formData.locationMap,
    formData.locationReference,
    formData.draft,
    generateLocationMap,
    steps
  ])

  // Trigger prop generation if user moves past uploadDocument step and references are missing
  useEffect(() => {
    const isUploadStep = steps[currentStep]?.key === 'uploadDocument'
    const hasPropMap =
      formData.propMap && Object.keys(formData.propMap).length > 0
    const hasPropReference =
      formData.props_reference &&
      Object.keys(formData.props_reference).length > 0
    const hasPendingDraft =
      formData.draft && formData.draft.id && formData.draft.status === 'pending'

    if (hasPropReference || !hasPropMap) {
      propGenerationTriggeredRef.current = false
    }

    if (
      !isUploadStep &&
      !isDraftMode &&
      !hasPendingDraft &&
      hasPropMap &&
      !hasPropReference &&
      !isGeneratingPropReference &&
      !propGenerationTriggeredRef.current
    ) {
      propGenerationTriggeredRef.current = true
      generatePropMap(formData.propMap)
    }
  }, [
    currentStep,
    isDraftMode,
    isGeneratingPropReference,
    formData.propMap,
    formData.props_reference,
    formData.draft,
    generatePropMap,
    steps
  ])

  // Trigger shot generation when user reaches shotList step and no shots exist
  useEffect(() => {
    const isShotListStep = steps[currentStep]?.key === 'shotList'
    const hasShots = formData.shotList && formData.shotList.length > 0

    if (
      isShotListStep &&
      !hasShots &&
      !formData.shotLoading &&
      !formData.shotGenerationError &&
      !shotsHook.isFetchingShots &&
      !isFetchingVideoData
    ) {
      shotsHook.fetchShots()
    }
  }, [
    currentStep,
    formData.shotList?.length,
    formData.shotLoading,
    formData.shotGenerationError,
    shotsHook.isFetchingShots,
    isFetchingVideoData,
    steps
  ])

  useEffect(() => {
    // 0. Initial Check: Is the hook even running?
    console.log('[AudioClustering] Effect triggered', {
      currentStep: steps[currentStep]?.key,
      isFetchingVideoData,
      audioStatus: formData.audio?.status
    })
    const isDialogueOnly = formData.audioMode === 'dialogue'

    if (isDialogueOnly) return

    if (isFetchingVideoData) {
      console.log('[AudioClustering] Exiting: Data is still fetching.')
      return
    }

    const currentStepKey = steps[currentStep]?.key
    const isAudioEligibleStep = [
      'videoStyle',
      'creativeDirection',
      'shotList',
      'characterVoiceover',
      'imageGeneration',
      'videoGeneration',
      'finalVideo'
    ].includes(currentStepKey)

    const audioStatus = formData.audio?.status
    const hasShots = formData.shotList?.length > 0

    // 1. Log Resume Polling logic
    if (audioStatus === 'pending' && formData.audio?.id && !isClusteringAudio) {
      console.log('[AudioClustering] Status is pending. Resuming polling...', {
        audioId: formData.audio.id
      })
      resumeAudioPolling(formData.audio.id)
      return
    }

    if (!isAudioEligibleStep) {
      console.log('[AudioClustering] Not on an eligible step. Resetting ref.')
      audioClusteringTriggeredRef.current = false
      return
    }

    // 2. Log Clustering Trigger logic
    const hasAnyAudioLink =
      hasShots && formData.shotList.some(scene => !!scene.audio_link)

    // Debugging the complex IF condition
    console.log('[AudioClustering] Evaluating trigger condition:', {
      hasAnyAudioLink,
      hasShots,
      selectedVoice: formData.selectedVoice,
      isClusteringAudio,
      isDraftMode,
      alreadyTriggered: audioClusteringTriggeredRef.current
    })

    if (
      !hasAnyAudioLink &&
      hasShots &&
      formData.selectedVoice &&
      !isClusteringAudio &&
      !audioClusteringTriggeredRef.current
    ) {
      console.log(
        '%c[AudioClustering] TRIGGERING CLUSTERING NOW',
        'color: green; font-weight: bold;'
      )
      audioClusteringTriggeredRef.current = true
      const formattedData = formatShotListForClustering(formData.shotList)
      initiateAudioClustering(formattedData, null, {
        voiceId: formData.selectedVoice
      })
    } else {
      console.log(
        '[AudioClustering] Trigger condition not met or already triggered.',
        {
          isApiPolling: isClusteringAudio
        }
      )
    }
  }, [
    isFetchingVideoData,
    currentStep,
    steps,
    formData.shotList,
    formData.selectedVoice,
    formData.audioMode,
    formData.audio?.id,
    formData.audio?.status,
    isClusteringAudio,
    isDraftMode,
    resumeAudioPolling,
    initiateAudioClustering,
    formatShotListForClustering
  ])

  const handleOverrideMode = (mode, audioMode, type) => {
    setFormData(prev => {
      let updatedShotList = prev.shotList

      if (type === 'video_mode') {
        // Clear generated assets but keep the shotlist structure
        updatedShotList = prev.shotList.map(scene => ({
          ...clearSceneCurrentAudio(scene, {
            audio_duration_estimated: scene.audio_duration_estimated
          }),
          shots: (scene.shots || []).map(shot => ({
            ...shot,
            imageSrc: null,
            vidSrc: null,
            audio_link: null
          }))
        }))
        message.info('Existing generated content cleared for the new mode.')
      } else if (type === 'audio_mode') {
        // Clear the whole shotlist
        updatedShotList = []
        message.info('Shotlist cleared for the new audio mode.')
      }

      return {
        ...prev,
        audioMode: audioMode || prev.audioMode,
        videoGenerationMode: mode,
        shotList: updatedShotList,
        calculatedShotList: null
      }
    })
  }

  const resetPendingStoryState = useCallback(() => {
    setPendingStoryAction(null)
    setPendingStoryText('')
    setPendingStoryLanguage(null)
  }, [])

  const pollTaskUntilDone = useCallback(async taskId => {
    if (!taskId) return null

    while (true) {
      const response = await getTaskStatus(taskId)
      const data = response?.data || {}

      if (data.status === 'success') {
        return data.output || data
      }

      if (data.status === 'failed') {
        throw new Error(data?.error || 'Story voiceover distribution failed')
      }

      await new Promise(resolve => setTimeout(resolve, 3000))
    }
  }, [])

  const normalizeDistributedShotList = useCallback(
    sceneData => {
      if (!sceneData) return []

      // Already in internal scene array format
      if (
        Array.isArray(sceneData) &&
        sceneData.every(scene => scene && Array.isArray(scene.shots))
      ) {
        return sceneData
      }

      // Typical API shape from clustering/distribution services
      if (typeof sceneData === 'object') {
        return formatSceneObj(sceneData)
      }

      return []
    },
    [formatSceneObj]
  )

  const extractDistributionOutput = useCallback(
    async responseData => {
      if (!responseData) return null

      if (responseData.task_id) {
        return pollTaskUntilDone(responseData.task_id)
      }

      return (
        responseData.output ||
        responseData.short_list ||
        responseData.shortlist ||
        responseData.shot_list ||
        responseData.scene_structure ||
        responseData
      )
    },
    [pollTaskUntilDone]
  )

  const extractDistributionFinalVideoUrl = useCallback(responseData => {
    if (!responseData || typeof responseData !== 'object') return ''

    const candidates = [
      responseData,
      responseData.output,
      responseData.scene_structure,
      responseData.short_list,
      responseData.shortlist,
      responseData.shot_list
    ].filter(Boolean)

    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'object') continue

      const finalVideoUrl =
        candidate.final_video_url ||
        candidate.finalVideoUrl ||
        candidate.video_url ||
        ''

      if (finalVideoUrl) {
        return finalVideoUrl
      }
    }

    return ''
  }, [])

  const createStoryCloneWithDistribution = useCallback(
    async onStepChange => {
      const updateStep =
        typeof onStepChange === 'function' ? onStepChange : () => { }

      const storyTextToUse = pendingStoryText || ''
      if (!storyTextToUse.trim()) {
        throw new Error('No translated story provided for cloning')
      }

      updateStep('cloning')
      const newVideoId = await cloneVideoWithStyle(formData.videoStyle)
      if (!newVideoId) {
        throw new Error('Failed to create cloned video')
      }

      updateStep('distributing')
      const sceneStructure = formatShotListForClustering(
        formData.shotList || []
      )
      const distributionResponse = await distributeStoryVoiceover({
        video_id: newVideoId,
        scene_structure: sceneStructure,
        story_text: storyTextToUse
      })

      const distributionData = distributionResponse?.data
      const distributionOutput = await extractDistributionOutput(
        distributionData
      )
      const distributedShotList = normalizeDistributedShotList(
        distributionOutput
      )
      const distributedFinalVideoUrl =
        extractDistributionFinalVideoUrl(distributionOutput) ||
        extractDistributionFinalVideoUrl(distributionData)

      const updatedCloneData = {
        ...formData,
        voiceoverText: storyTextToUse,
        language: pendingStoryLanguage || formData.language || 'en',
        shotList: distributedShotList,
        calculatedShotList: null,
        audio: {
          id: null,
          status: 'idle'
        },
        video: {
          id: null,
          status: 'idle'
        },
        draft: {
          id: null,
          status: 'pending'
        },
        finalVideo: distributedFinalVideoUrl,
        final_videos_history: [],
        transitions: {}
      }

      const payload = getPayload(updatedCloneData)
      payload.video_url = distributedFinalVideoUrl || ''
      payload.final_videos_history = '[]'
      payload.transitions = '[]'

      updateStep('saving')
      await updateAiVideo(newVideoId, payload)

      updateStep('done')
      return newVideoId
    },
    [
      pendingStoryText,
      pendingStoryLanguage,
      cloneVideoWithStyle,
      formData,
      formatShotListForClustering,
      extractDistributionOutput,
      extractDistributionFinalVideoUrl,
      normalizeDistributedShotList,
      getPayload,
      updateAiVideo
    ]
  )

  const handleBeforeAddStory = useCallback(
    (action, storyToUse = '', storyLanguage = null) => {
      if (formData.shotList && formData.shotList.length > 0) {
        setPendingStoryAction(() => action)
        setPendingStoryText(storyToUse || '')
        setPendingStoryLanguage(storyLanguage || formData.language || 'en')
        setShowEditOptions(true)
      } else {
        action()
      }
    },
    [formData.shotList, formData.language]
  )

  const handleCloneAndProceed = async () => {
    setShowEditOptions(false)
    setIsStoryCloneDistributing(true)
    setStoryCloneStep('cloning')
    try {
      const newVideoId = await createStoryCloneWithDistribution(
        setStoryCloneStep
      )
      if (newVideoId) {
        message.success({
          content: 'Video cloned and story voiceover distributed successfully',
          key: 'story-clone-distribute'
        })
        resetPendingStoryState()
        history.push(`/video-create/${newVideoId}/shotList`)
        window.location.reload()
      }
    } catch (e) {
      console.error('Clone + distribute failed:', e)
      message.error({
        content:
          e?.message ||
          'Failed to clone video and distribute translated story voiceover',
        key: 'story-clone-distribute'
      })
    } finally {
      setIsStoryCloneDistributing(false)
      setStoryCloneStep('idle')
    }
  }

  const handleCloseEditOptions = useCallback(() => {
    setShowEditOptions(false)
    resetPendingStoryState()
  }, [resetPendingStoryState])

  const cloneStatusMeta = (() => {
    if (isStoryCloneDistributing) {
      if (storyCloneStep === 'distributing') {
        return {
          stepLabel: 'Step 2 of 3',
          title: 'Generating New Shotlist...',
          description:
            'We are distributing the translated story and creating updated scene shots.'
        }
      }
      if (storyCloneStep === 'saving') {
        return {
          stepLabel: 'Step 3 of 3',
          title: 'Saving New Project...',
          description:
            'Finalizing the cloned video instance with the new translated shortlist.'
        }
      }
      return {
        stepLabel: 'Step 1 of 3',
        title: 'Cloning Video...',
        description: 'Please wait while we create a new copy of your project.'
      }
    }

    return {
      stepLabel: null,
      title: 'Cloning Video...',
      description: 'Please wait while we create a new copy.'
    }
  })()

  const handleNewAndProceed = () => {
    setShowEditOptions(false)
    resetPendingStoryState()
    const { videoId: _, ...rest } = defaultData
    setFormData(rest)
    // For new video, we redirect to the creation page
    history.push('/video-create')
  }

  const handleClearAndProceed = () => {
    setShowEditOptions(false)
    setFormData(prev => ({
      ...prev,
      shotList: [],
      selectedVoice: null,
      audio: defaultData.audio,
      video: defaultData.video,
      draft: defaultData.draft,
      calculatedShotList: null,
      durationsReady: false
    }))
    if (pendingStoryAction) {
      pendingStoryAction()
    }
    resetPendingStoryState()
  }

  const handleCloneMode = async (mode, audioMode, type) => {
    try {
      // Trigger cloning state
      handleSaveVideoData.cancel() // Stop any pending auto-saves
      message.loading({ content: 'Cloning project...', key: 'cloning' })

      // Create a payload that reflects the new mode and required clearings
      const payload = getPayload({
        ...formData,
        videoGenerationMode: mode,
        audioMode: audioMode,
        shotList:
          type === 'audio_mode'
            ? []
            : (formData.shotList || []).map(scene => ({
              ...clearSceneCurrentAudio(scene, {
                audio_duration_estimated: scene.audio_duration_estimated,
                audio_history: []
              }),
              shots: (scene.shots || []).map(shot => ({
                ...shot,
                imageSrc: null,
                vidSrc: null,
                audio_link: null
              }))
            }))
      })

      payload.video_url = ''
      payload.final_videos_history = '[]'
      payload.transitions = '[]'

      const response = await createAiVideo(payload)
      const newId = response?.data?.id

      if (newId) {
        // Persist full cloned state (character maps, wardrobe, refs, etc.).
        // Some backends return the id on create but require a follow-up update
        // to reliably store the complete payload.
        await updateAiVideo(newId, payload)

        message.success({
          content: 'Project cloned successfully!',
          key: 'cloning'
        })
        Modal.success({
          title: 'Project Cloned Successfully',
          content:
            'You are now working on a new project with the selected mode.',
          onOk: () => {
            history.push(`/video-create/${newId}/uploadDocument`)
            window.location.reload() // Ensure fresh state
          },
          centered: true
        })
      }
    } catch (error) {
      console.error('Cloning failed:', error)
      message.error({ content: 'Failed to clone project', key: 'cloning' })
    }
  }

  const handleSelectMode = (mode, audioMode) => {
    setShowModeSelectionPage(false)
    setFormData(prev => ({
      ...prev,
      audioMode: audioMode || prev.audioMode,
      videoGenerationMode: mode
    }))
    if (mode === 'manual') {
      if (steps[currentStep]?.key === 'uploadDocument') {
        handleNext()
      }
    } else if (mode === 'draft') {
      setIsDraftMode(true)
    }
  }

  const handleFinishDraft = async draftData => {
    try {
      const updatedFormData = {
        ...formData,
        shotList: (draftData.shot_list || []).map(scene =>
          normalizeSceneAudioHistory({
            ...scene,
            audio_link: scene.audio_link || null
          })
        ),
        locationMap: parseJSONSafely(draftData.location_map),
        locationReference: parseJSONSafely(draftData.location_reference),
        propMap: parseJSONSafely(draftData.prop_map),
        props_reference: parseJSONSafely(
          draftData.props_reference || draftData.prop_reference
        ),
        characterMap: parseJSONSafely(draftData.character_map),
        selectedCharacter: parseJSONSafely(draftData.selected_character),
        selectedVoice: draftData.selected_voice,
        aspectRatio: draftData.aspect_ratio || '16:9',
        videoStyle: draftData.video_style,
        outfits_reference: parseJSONSafely(draftData.outfits_reference),
        transitions: parseJSONSafely(draftData.transitions),
        finalVideo: draftData.video_url || '',
        draft: {
          ...formData.draft,
          status: 'completed'
        }
      }
      await handleUpdateVideoData(updatedFormData)

      await fetchVideoData()

      setIsDraftMode(false)

      navigateToStep(3)
      message.success('Draft processed successfully! Review your scenes below.')
    } catch (error) {
      console.error('Failed to finalize draft:', error)
      message.error('Failed to process draft results.')
    }
  }

  // Handle video style change with Clone/Override
  const handleVideoStyleChange = async (
    newStyle,
    shouldClone,
    type = 'video_style'
  ) => {
    if (shouldClone) {
      try {
        const newVideoId = await cloneVideoWithStyle(newStyle, null, type)
        const stepKey = steps[currentStep]?.key || 'videoStyle'

        Modal.success({
          title: 'Video Cloned Successfully',
          content:
            'You are now editing a new copy of the video. The original video remains unchanged.',
          onOk: () => {
            history.push(`/video-create/${newVideoId}/${stepKey}`)
          },
          centered: true
        })
      } catch (error) {
        message.error('Failed to clone video')
      }
    } else {
      const hasGeneratedContent =
        formData.shotList &&
        formData.shotList.length > 0 &&
        formData.shotList.some(scene => scene.shots.some(shot => shot.imageSrc))

      if (type === 'shot_style') {
        setFormData(prev => ({
          ...prev,
          shotGenerationStyle: newStyle,
          editingStyle: true,
          shotList: [],
          calculatedShotList: null,
          locationMap: {},
          locationReference: {},
          characterMap: {}
        }))

        // Trigger new shot generation with the new style id
        setTimeout(() => {
          shotsHook.fetchShots(
            newStyle?.id || newStyle?.raw?.id || newStyle?.key
          )
        }, 100)

        message.info('Existing shots removed. Regenerating with new style...')
      } else {
        setFormData(prev => ({
          ...prev,
          videoStyle: newStyle,
          ...(hasGeneratedContent && {
            shotList: prev.shotList.map(scene => ({
              ...scene,
              shots: scene.shots.map(shot => ({
                ...shot,
                imageSrc: null,
                vidSrc: null
              }))
            }))
          })
        }))

        if (hasGeneratedContent) {
          message.info('Images will be regenerated with new style')
        }
      }
    }
  }

  // Handle voice change with Clone/Override
  const handleVoiceChangeWithClone = async (
    voiceId,
    shouldClone,
    characterKey = null
  ) => {
    if (shouldClone) {
      try {
        // Clone video with new voice only (pass null for style to preserve images)
        const newVideoId = await cloneVideoWithStyle(null, voiceId)
        const stepKey = steps[currentStep]?.key || 'narrationVoiceover'

        Modal.success({
          title: 'Video Cloned Successfully',
          content:
            'You are now editing a new copy of the video with the new voice. The original video remains unchanged.',
          onOk: () => {
            history.push(`/video-create/${newVideoId}/${stepKey}`)
          },
          centered: true
        })
      } catch (error) {
        message.error('Failed to clone video')
      }
    } else {
      // Override: use the existing handleVoiceChange from the hook
      handleVoiceChange(voiceId, false, characterKey)
    }
  }

  // Component ref handlers
  const handleOpenVideoEditor = () => {
    if (videoGenerationRef.current?.openVideoEditor) {
      videoGenerationRef.current.openVideoEditor()
    } else {
      console.error('Video generation function not available')
    }
  }

  // Machine state handlers
  const startMachine = async () => await changeMachineState(1)
  const stopMachine = async () => await changeMachineState(0)

  const showModalMessage = ({
    title = 'Info',
    content = '',
    type = 'info'
  }) => {
    Modal[type]({
      title,
      content,
      centered: true
    })
  }

  const handleContinueCreationMode = () => {
    if (!pendingCreationMode) {
      message.warning('Please choose Basic or Advanced to continue.')
      return
    }

    setFormData(prev => ({
      ...prev,
      aiVideoFlowMode: pendingCreationMode,
      creativeDirection: {
        ...(prev.creativeDirection || {}),
        flowMode: pendingCreationMode
      }
    }))
  }

  const regenerateShotListInCurrentVideo = () => {
    setFormData(prev => ({
      ...prev,
      shotList: [],
      calculatedShotList: null,
      shotsApiRes: null,
      shotGenerationError: null,
      audio: defaultData.audio,
      video: defaultData.video,
      draft: defaultData.draft,
      finalVideo: '',
      final_videos_history: [],
      transitions: {}
    }))

    navigateToStep('shotList')

    window.setTimeout(() => {
      shotsHook.fetchShots()
    }, 0)
  }

  const handleRegenerateShotListFromCreative = () => {
    setShowCreativeShotRegenerateModal(true)
  }

  const handleConfirmCreativeShotRegenerate = () => {
    setShowCreativeShotRegenerateModal(false)
    regenerateShotListInCurrentVideo()
    message.info(
      'Current Shot List cleared. Regenerating from the Creative Guideline...'
    )
  }

  const handleCloneCreativeShotRegenerate = async () => {
    setIsCreativeShotCloneRegenerating(true)

    try {
      const clonedFormData = {
        ...formDataRef.current,
        shotList: [],
        calculatedShotList: null,
        shotsApiRes: null,
        shotGenerationError: null,
        audio: defaultData.audio,
        video: defaultData.video,
        draft: defaultData.draft,
        finalVideo: '',
        final_videos_history: [],
        transitions: {}
      }
      const payload = getPayload(clonedFormData)

      payload.shotList = []
      payload.video_url = ''
      payload.final_videos_history = '[]'
      payload.transitions = '[]'

      const response = await createAiVideo(payload)
      const newVideoId = response?.data?.id

      if (!newVideoId) {
        throw new Error('Failed to create cloned video')
      }

      await updateAiVideo(newVideoId, payload)

      setShowCreativeShotRegenerateModal(false)
      message.success('Video cloned. Opening the new Shot List...')
      history.push(`/video-create/${newVideoId}/shotList`)
      window.location.reload()
    } catch (error) {
      console.error('Failed to clone video for Shot List regeneration:', error)
      message.error(
        error?.message || 'Failed to clone video for Shot List regeneration'
      )
    } finally {
      setIsCreativeShotCloneRegenerating(false)
    }
  }

  // Handle safe navigation
  const handleSafePrevious = () => {
    const previousStepKey = steps[Math.max(currentStep - 1, 0)]?.key
    const isFirstStepAfterUpload =
      currentStep > 0 && previousStepKey === 'uploadDocument'

    if (isFirstStepAfterUpload) {
      setShowModeSelectionPage(true)
      return
    }
    handlePrevious()
  }

  const handleSafeNext = () => {
    const currentStepKey = steps[currentStep]?.key
    const isImageGenerationStep = currentStepKey === 'imageGeneration'
    const isCreativeDirectionStep = currentStepKey === 'creativeDirection'
    const shouldValidateSceneAudio = formData.audioMode !== 'dialogue'

    if (isCreativeDirectionStep) {
      const hasFinalizedCreativeGuideline = Boolean(
        formData.creativeDirection?.finalized ||
        formData.creativeDirection?.bible
      )

      if (!hasFinalizedCreativeGuideline) {
        Modal.confirm({
          title: 'Continue without Creative Guideline?',
          content: (
            <div>
              <p>
                This AI Director step is optional, so you can continue to Shot
                List now.
              </p>
              <p>
                If you skip it, the shot list will not use the unfinished
                creative guideline, including any incomplete vision, character,
                wardrobe, location, or storyboard direction from this step.
              </p>
            </div>
          ),
          okText: 'Continue to Shot List',
          cancelText: 'Stay here',
          centered: true,
          onOk: handleNext
        })
        return
      }
    }

    if (
      isClusteringAudio &&
      isImageGenerationStep &&
      shouldValidateSceneAudio
    ) {
      showModalMessage({
        title: 'Audio Generation in Progress',
        content:
          'Audio generation is currently in progress. Please wait for it to complete before proceeding.',
        type: 'info'
      })
      return
    }

    // Check for mandatory voice selection (Narrator)
    if (steps[currentStep]?.key === 'narrationVoiceover') {
      const missingNarrator = !formData.selectedVoice
      if (missingNarrator) {
        setVoiceWarningData({
          missingNarrator: true,
          charactersWithoutVoice: []
        })
        setShowVoiceWarningModal(true)
        return
      }
    }

    // Check for mandatory voice selection (Characters)
    if (steps[currentStep]?.key === 'characterVoiceover') {
      const characterMap = formData.characterMap || {}
      const characterVoices = formData.characterVoices || {}
      const charactersWithoutVoice = Object.entries(characterMap)
        .filter(([key]) => !characterVoices[key])
        .map(([, name]) => (typeof name === 'object' ? name.name : name))

      if (charactersWithoutVoice.length > 0) {
        setVoiceWarningData({
          missingNarrator: false,
          charactersWithoutVoice
        })
        setShowVoiceWarningModal(true)
        return
      }
    }

    // Always show mode selection page after 'uploadDocument' step
    if (steps[currentStep]?.key === 'uploadDocument') {
      setShowModeSelectionPage(true)
      return
    }

    // Check for missing audio links in scenes
    if (
      isImageGenerationStep &&
      shouldValidateSceneAudio &&
      formData.shotList?.length > 0
    ) {
      const isDialogueAndNarrationMode =
        formData.audioMode === 'dialogue_and_narration'

      const sceneHasShotDialogue = scene =>
        (scene?.shots || []).some(shot =>
          Object.values(shot?.dialogue || {}).some(line => {
            if (typeof line === 'string') return line.trim().length > 0
            if (
              line &&
              typeof line === 'object' &&
              typeof line.text === 'string'
            )
              return line.text.trim().length > 0
            return false
          })
        )

      const scenesMissingAudio = formData.shotList.filter(scene => {
        if (scene.audio_link) return false
        if (isDialogueAndNarrationMode && sceneHasShotDialogue(scene))
          return false
        return true
      })

      if (scenesMissingAudio.length > 0) {
        const missingCount = scenesMissingAudio.length
        const sceneLabel = missingCount === 1 ? 'scene is' : 'scenes are'
        const previewSceneTitles = scenesMissingAudio
          .slice(0, 3)
          .map(scene => scene.title || 'Untitled scene')
        const hasMoreScenes = missingCount > previewSceneTitles.length

        showModalMessage({
          title: 'Audio Missing',
          content: (
            <div>
              {isDialogueAndNarrationMode ? (
                <p>
                  {missingCount} {sceneLabel} missing required audio setup. For{' '}
                  <strong>Dialogue + Narration</strong>, each scene must have
                  either a scene audio link or at least one shot with non-empty
                  dialogue text.
                </p>
              ) : (
                <p>
                  {missingCount} {sceneLabel} missing generated audio links.
                </p>
              )}
              <p>
                {previewSceneTitles.join(', ')}
                {hasMoreScenes ? ', and more.' : '.'}
              </p>
              <p>
                Please generate audio for the missing scenes to proceed to the
                next step.
              </p>
            </div>
          ),
          centered: true
        })
        return
      }
    }

    handleNext()
  }

  // Render step content
  const renderStepContent = () => {
    const activeStep = steps[currentStep]
    if (!activeStep) return null
    const { key, component: StepComponent } = activeStep

    const commonProps = {
      icon: steps[currentStep].icon,
      formData,
      setFormData,
      onValidationChange: isValid => updateStepValidation(key, isValid),
      onProceed: handleSafeNext,
      onGoBack: handleSafePrevious,
      isGeneratingLocationReference,
      onRegenerateShots: () => shotsHook.fetchShots(),
      onSkipToShotList: handleNext,
      onRegenerateShotListFromCreative: handleRegenerateShotListFromCreative,
      isClusteringAudio,
      isClusteringAudio,
      videoId,
      onBeforeAddStory: handleBeforeAddStory
    }

    // VideoGeneration (last step)
    if (steps[currentStep]?.key === 'videoGeneration') {
      return (
        <StepComponent
          ref={videoGenerationRef}
          {...commonProps}
          videoGenerating={videoGenerating}
          generatingVideoForAllScenes={generatingVideoForAllScenes}
          handleRegenerateVideo={handleRegenerateVideo}
          handleStopVideoGeneration={handleStopVideoGeneration}
          handleRetryFailedVideos={handleRetryFailedVideos}
          handleGenerateFullVideo={generateAllVideos}
          updateShotDataRef={updateShotDataRef}
          videoProvider={videoProvider}
          setScenes={setScenes}
          isCalculatingDurations={isCalculatingDurations || calculationStarting}
          calculationProgress={calculationProgress}
          onStartDurationCalculation={handleStartDurationCalculation}
          // Final video props
          isGeneratingFinalVideo={isGeneratingFinalVideo}
          finalVideoError={finalVideoError}
          onGenerateFinalVideo={generateFinalVideo}
          setFinalVideoError={setFinalVideoError}
          // Image generation props for inline editing
          handleRegenerateImage={handleRegenerateImage}
          imageProvider={imageProvider}
          isImageGenerationLoading={isImageGenerationLoading}
          isFetchingCharacters={isFetchingCharacters}
        />
      )
    }

    // ShotList
    if (key === 'shotList') {
      return (
        <StepComponent
          ref={imageGenerationRef}
          {...commonProps}
          onEditLocationReference={handleEditLocationReference}
          fetchingShots={shotsHook.isFetchingShots}
          shotsGenerationProgress={shotsHook.shotsGenerationProgress}
          isClustering={shotsHook.isClustering}
          clusteringProgress={shotsHook.clusteringProgress}
          locationGenerationProgress={locationGenerationProgress}
          locationGenerationStatus={locationGenerationStatus}
          fetchShots={fetchShots}
          formatSceneObj={formatSceneObj}
          onRegenerateLocationReference={() =>
            generateLocationMap(formData.locationMap)
          }
          onAddLocationReference={handleAddLocationReference}
          isFetchingCharacters={isFetchingCharacters}
          characters={characters}
          setCharacters={setCharacters}
          fetchCharacters={fetchCharacters}
          onBatchUpdateLocations={handleBatchUpdateLocations}
          onNavigateToStep={navigateToStep}
          // Prop Reference props
          isGeneratingPropReference={isGeneratingPropReference}
          propGenerationProgress={propGenerationProgress}
          propGenerationStatus={propGenerationStatus}
          onRegeneratePropReference={() => generatePropMap(formData.propMap)}
          onAddPropReference={handleAddPropReference}
          onEditPropReference={handleEditPropReference}
          onBatchUpdateProps={handleBatchUpdateProps}
        />
      )
    }

    // VideoStyle (step 2)
    if (steps[currentStep]?.key === 'videoStyle') {
      return (
        <StepComponent
          {...commonProps}
          onStyleChange={handleVideoStyleChange}
        />
      )
    }

    // Final Result (step 7)
    if (steps[currentStep]?.key === 'finalVideo') {
      return <StepComponent {...commonProps} />
    }

    // Default for other steps
    return StepComponent ? (
      <StepComponent
        {...commonProps}
        imageProvider={imageProvider}
        isImageGenerationLoading={isImageGenerationLoading}
        setIsImageGenerationLoading={setIsImageGenerationLoading}
        generateAllImages={generateAllImages}
        handleRegenerateImage={handleRegenerateImage}
        retryFailedImages={retryFailedImages}
        audioClusteringProgress={audioClusteringProgress}
        audioClusteringStatus={audioClusteringStatus}
        onNavigateToStep={navigateToStep}
        voices={voices}
        setVoices={setVoices}
        onVoiceChange={handleVoiceChangeWithClone}
        handleFileUpload={handleFileUpload}
        isUploadingImage={isUploadingImage}
        isFetchingCharacters={isFetchingCharacters}
        onStopGeneration={handleStopAudioGeneration}
        onRetryAudioClustering={handleRetryAudioClustering}
      />
    ) : null
  }

  const allScenesGenerated = isAllScenesGenerated(formData.shotList)
  const currentStepKey = steps[currentStep]?.key
  const isCreativeDirectionStep = currentStepKey === 'creativeDirection'
  const isNextDisabled =
    !isCreativeDirectionStep && !stepsValidation[currentStepKey]

  console.log({ formData })

  const shouldShowCreationModeSelection =
    !videoId && !formData.aiVideoFlowMode && !isDraftMode

  if (showModeSelectionPage) {
    return (
      <div className="video-generation">
        <ModeSelectionPage
          onSelectMode={handleSelectMode}
          onOverrideMode={handleOverrideMode}
          onCloneMode={handleCloneMode}
          isSavingVideoData={isSavingVideoData}
          isCloning={isCloning}
          videoId={videoId}
          initialMode={formData.videoGenerationMode}
          initialAudioMode={formData.audioMode}
          shotList={formData.shotList}
          onGoBack={() => {
            const previousStepKey = steps[Math.max(currentStep - 1, 0)]?.key
            const isFirstStepAfterUpload =
              currentStep > 0 && previousStepKey === 'uploadDocument'

            setShowModeSelectionPage(false)
            if (isFirstStepAfterUpload) {
              handlePrevious()
            }
          }}
        />
      </div>
    )
  }

  if (shouldShowCreationModeSelection) {
    return (
      <CreationModeSelection
        selectedMode={pendingCreationMode}
        onSelectMode={setPendingCreationMode}
        onContinue={handleContinueCreationMode}
        onGoBack={handleGoBack}
      />
    )
  }

  if (isDraftMode) {
    return (
      <div className="video-generation">
        <DraftModePage
          formData={formData}
          setFormData={setFormData}
          onExitDraftMode={() => {
            setIsDraftMode(false)
            // If they cancel, maybe we want to go back or stay here?
            // For now just return to manual mode view
          }}
          videoId={videoId}
          onFinishDraft={handleFinishDraft}
          voices={voices}
          setVoices={setVoices}
          onVoiceChange={handleVoiceChange}
          characters={characters}
          setCharacters={setCharacters}
          isFetchingCharacters={isFetchingCharacters}
          fetchCharacters={fetchCharacters}
          draftShotsHook={draftShotsHook}
          fetchShots={fetchShots}
          formatSceneObj={formatSceneObj}
        />
      </div>
    )
  }

  return (
    <Spin spinning={isFetchingVideoData}>
      {error ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            padding: '2rem'
          }}
        >
          <Icon
            type="exclamation-circle"
            style={{ fontSize: '48px', color: '#ff4d4f', marginBottom: '16px' }}
          />
          <h2 style={{ marginBottom: '8px' }}>
            {error === 'NOT_FOUND' ? 'Video Not Found' : 'Error Loading Video'}
          </h2>
          <p style={{ color: '#666', marginBottom: '24px' }}>
            {error === 'NOT_FOUND'
              ? 'The video you are looking for does not exist or has been deleted.'
              : 'There was an unexpected error getting the video data.'}
          </p>
          <Button type="primary" onClick={fetchVideoData}>
            Retry
          </Button>
        </div>
      ) : (
        <>
          <div
            className={`video-generation${isCreativeDirectionStep
              ? ' video-generation--creative-direction'
              : ''
              }`}
          >
            <div className="video-generation__header">
              <div className="video-generation__header_top">
                <div className="video-generation__header_info">
                  <div
                    className="video-generation__header_info-icon"
                    onClick={handleGoBack}
                  >
                    <Tooltip title="Back to Video List">
                      <Icon type="arrow-left" />
                    </Tooltip>
                  </div>
                  <div
                    className="video-generation__header_info-text"
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '16px'
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: '11px',
                          fontWeight: 700,
                          color: '#5057d5',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <span
                          style={{
                            background: '#eff0ff',
                            padding: '2px 8px',
                            borderRadius: '10px'
                          }}
                        >
                          Step {currentStep + 1} / {steps.length}
                        </span>
                        <span style={{ color: '#8c8c8c' }}>•</span>
                        <span>{steps[currentStep]?.title}</span>
                      </div>

                      <div
                        className="header-container-text"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}
                      >
                        <h1
                          className="header-title-text"
                          style={{
                            marginBottom: 0,
                            fontSize: '24px',
                            fontWeight: 700,
                            color: '#111827',
                            letterSpacing: '-0.5px',
                            lineHeight: 1.2
                          }}
                        >
                          AI Video Generator
                        </h1>
                      </div>
                      <p
                        className="header-subtitle-text"
                        style={{
                          marginBottom: 0,
                          fontSize: '13px',
                          color: '#6b7280',
                          marginTop: '2px'
                        }}
                      >
                        Follow steps to generate AI video
                      </p>
                    </div>

                    {/* Status Indicators Centered Vertically */}
                    {isGeneratingFinalVideo && (
                      <Button
                        size="small"
                        loading
                        type="primary"
                        ghost
                        style={{
                          borderRadius: '12px',
                          fontSize: '12px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        onClick={() => setShowGenerationModal(true)}
                      >
                        Generating...
                      </Button>
                    )}
                  </div>
                </div>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <NewHeader />
                </div>
              </div>

              <div className="video-generation__progress-container">
                <div
                  className="video-generation__progress-bar"
                  style={{
                    width: `${((currentStep + 1) / steps.length) * 100}%`
                  }}
                />
              </div>
            </div>

            <div className="video-generation__container">
              <div
                className={`video-generation__content${isCreativeDirectionStep
                  ? ' video-generation__content--creative-direction'
                  : ''
                  }`}
              >
                {renderStepContent()}
              </div>

              <div className="video-generation__navigation">
                <div className="video-generation__navigation-left">
                  {currentStep > 0 && (
                    <Button
                      size="large"
                      onClick={handleSafePrevious}
                      className="nav-button"
                    >
                      <Icon type="left" />
                      {steps[currentStep]?.key === 'videoStyle'
                        ? 'Mode Selection'
                        : getPreviousNextStepTitles().previousStepTitle}
                    </Button>
                  )}
                </div>

                <div className="video-generation__navigation-right">
                  {currentStep > 0 && (
                    <Button
                      size="large"
                      loading={isSavingVideoData}
                      onClick={handleSaveVideoData}
                      className="nav-button nav-button--save"
                    >
                      {!isSavingVideoData && <Icon type="save" />}
                      Save
                    </Button>
                  )}

                  {shouldShowNextButton() && (
                    <Button
                      type="primary"
                      size="large"
                      onClick={handleSafeNext}
                      disabled={isNextDisabled}
                      className="nav-button"
                    >
                      Next: {getPreviousNextStepTitles().nextStepTitle}
                      <Icon type="right" />
                    </Button>
                  )}

                  {steps[currentStep]?.key === 'videoGeneration' && (
                    <Button
                      type="primary"
                      size="large"
                      shape="round"
                      icon="play-circle"
                      disabled={!allScenesGenerated}
                      onClick={handleOpenVideoEditor}
                    >
                      Generate Full Video
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Modal
            visible={isCloning || isStoryCloneDistributing}
            footer={null}
            closable={false}
            centered
            width={300}
            bodyStyle={{ padding: '32px 24px', textAlign: 'center' }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px'
              }}
            >
              <Spin size="large" />
              <h3
                style={{
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#1f2937'
                }}
              >
                {cloneStatusMeta.title}
              </h3>
              <p
                style={{ color: '#6b7280', fontSize: '14px', marginBottom: 0 }}
              >
                {cloneStatusMeta.description}
              </p>
              {cloneStatusMeta.stepLabel ? (
                <div
                  style={{
                    color: '#94a3b8',
                    fontSize: '12px',
                    fontWeight: 600
                  }}
                >
                  {cloneStatusMeta.stepLabel}
                </div>
              ) : null}
            </div>
          </Modal>
        </>
      )}

      {/* Global Final Video Generation Status Modal */}
      {showGenerationModal && isGeneratingFinalVideo && (
        <GenerationStatusModal
          visible={showGenerationModal && isGeneratingFinalVideo}
          status="loading"
          small
          title="Generating Final Video"
          subTitle="Please wait while we process your request. This may take a few minutes."
          onClose={() => {
            Modal.confirm({
              title: 'Generation in progress',
              content:
                'Final video generation is still running in the background. Are you sure you want to close this status window?',
              okText: 'Yes, Close',
              cancelText: 'Stay',
              centered: true,
              onOk: () => {
                setShowGenerationModal(false)
              }
            })
          }}
        />
      )}

      {/* Global Final Video Player Modal */}
      {showFinalVideoModal && formData.finalVideo && (
        <AIVideoModal
          visible={showFinalVideoModal}
          videoUrl={formData.finalVideo}
          title="Your AI Generated Video"
          onClose={() => setShowFinalVideoModal(false)}
        />
      )}
      <VoiceSelectionRequiredModal
        visible={showVoiceWarningModal}
        onCancel={() => setShowVoiceWarningModal(false)}
        missingNarrator={voiceWarningData.missingNarrator}
        charactersWithoutVoice={voiceWarningData.charactersWithoutVoice}
      />
      <Modal
        maskClosable={false}
        title="Regenerate Shot List?"
        visible={showCreativeShotRegenerateModal}
        centered
        width={760}
        closable={!isCreativeShotCloneRegenerating}
        onCancel={() => {
          if (!isCreativeShotCloneRegenerating) {
            setShowCreativeShotRegenerateModal(false)
          }
        }}
        footer={
          <div className="story-replace-modal-footer">
            <Button
              className="story-replace-modal-footer__btn"
              disabled={isCreativeShotCloneRegenerating}
              onClick={() => setShowCreativeShotRegenerateModal(false)}
            >
              Cancel
            </Button>
            <Button
              className="story-replace-modal-footer__btn"
              type="primary"
              loading={isCreativeShotCloneRegenerating}
              onClick={handleCloneCreativeShotRegenerate}
            >
              Clone as New Video
            </Button>
            <Button
              className="story-replace-modal-footer__btn"
              type="danger"
              disabled={isCreativeShotCloneRegenerating}
              onClick={handleConfirmCreativeShotRegenerate}
            >
              Clear & Regenerate
            </Button>
          </div>
        }
      >
        <p>
          This will clear the current Shot List and generate a new one from the
          completed Creative Guideline.
        </p>
        <p>
          If you continue on this video, you may lose existing shot edits,
          generated images, generated videos, timing changes, and any downstream
          media connected to the current Shot List.
        </p>
        <p>
          Choose <strong>Clone as New Video</strong> to keep this version
          unchanged and regenerate the Shot List on a copy.
        </p>
      </Modal>
      <Modal
        maskClosable={false}
        title="Existing Shot List Detected"
        visible={showEditOptions}
        centered
        width={760}
        onCancel={handleCloseEditOptions}
        footer={
          <div className="story-replace-modal-footer">
            <Button
              className="story-replace-modal-footer__btn"
              onClick={handleNewAndProceed}
            >
              Start New Video
            </Button>
            <Button
              className="story-replace-modal-footer__btn"
              type="primary"
              loading={isCloning || isStoryCloneDistributing}
              onClick={handleCloneAndProceed}
            >
              Clone as New Video
            </Button>
            <Button
              className="story-replace-modal-footer__btn"
              type="danger"
              onClick={handleClearAndProceed}
            >
              Overwrite Current Video
            </Button>
          </div>
        }
      >
        <p>
          You have already generated a story and shot list for this video. To
          apply your new story, please choose how you would like to proceed:
        </p>
      </Modal>
    </Spin>
  )
}

export default VideoAi
